import { useState } from 'react';
import { LIMITS } from '@elixir/shared';
import type { RoomContext } from './RoomView';

/** Quizmaster only: move players between teams, mute, remove, add/remove teams. */
export function PeoplePanel({ ctx, speakers }: { ctx: RoomContext; speakers: string[] }) {
  const { quiz, send } = ctx;
  const [confirmKick, setConfirmKick] = useState<string | null>(null);
  const players = quiz.participants.filter((p) => p.role === 'player');
  const groups = [
    ...quiz.teams.map((t) => ({ id: t.id as string | null, name: t.name, color: t.color })),
    { id: null, name: 'No team', color: 'var(--ink-faint)' },
  ];

  return (
    <section className="people-panel" aria-label="Teams and people">
      {groups.map((g) => {
        const members = players.filter((p) => p.teamId === g.id);
        if (g.id === null && members.length === 0) return null;
        return (
          <div key={g.id ?? 'none'} className="group" style={{ ['--c' as string]: g.color }}>
            <div className="group-head">
              <span className="swatch" aria-hidden />
              <h3>{g.name}</h3>
              <span className="muted">{members.length}</span>
              {g.id && quiz.teams.length > LIMITS.minTeams && (
                <button
                  className="link-btn subtle"
                  onClick={() => {
                    if (confirm(`Remove ${g.name}? Its players will have no team and its score is deleted.`)) {
                      send({ type: 'removeTeam', teamId: g.id! });
                    }
                  }}
                >
                  Remove team
                </button>
              )}
            </div>
            {members.length === 0 && <p className="muted small">Nobody yet.</p>}
            <ul className="people">
              {members.map((p) => (
                <li key={p.id} className={`person manage${p.online ? '' : ' offline'}`}>
                  <span className={`avatar${speakers.includes(p.id) ? ' talking' : ''}`}>{p.name.slice(0, 1).toUpperCase()}</span>
                  <span className="person-name">
                    {p.name}
                    {!p.online && <span className="muted"> (away)</span>}
                    {p.whispering && <span className="huddle"> whispering</span>}
                  </span>
                  <div className="manage-controls">
                  <select
                    aria-label={`Team for ${p.name}`}
                    value={p.teamId ?? ''}
                    onChange={(e) => send({ type: 'assignTeam', participantId: p.id, teamId: e.target.value || null })}
                  >
                    {quiz.teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                    <option value="">No team</option>
                  </select>
                  <button className="mini-btn" onClick={() => send({ type: 'mute', participantId: p.id })}>
                    Mute
                  </button>
                  {confirmKick === p.id ? (
                    <button
                      className="mini-btn danger"
                      onClick={() => {
                        send({ type: 'kick', participantId: p.id });
                        setConfirmKick(null);
                      }}
                      onBlur={() => setConfirmKick(null)}
                      autoFocus
                    >
                      Confirm
                    </button>
                  ) : (
                    <button className="mini-btn" onClick={() => setConfirmKick(p.id)}>
                      Remove
                    </button>
                  )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {quiz.teams.length < LIMITS.maxTeams && (
        <button className="btn btn-quiet add-team" onClick={() => send({ type: 'addTeam' })}>
          Add a team
        </button>
      )}
      {players.length === 0 && <p className="muted">Share the invite link. Players appear here and are spread across teams as they join.</p>}
    </section>
  );
}
